import { useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Heart, Bug, HelpCircle, BookOpen, Baby, CreditCard, Sparkles, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/hooks/use-language";
import { submitFeedback } from "@/lib/feedback.functions";

type Cat = "bug" | "confusing_screen" | "content" | "child_reaction" | "billing" | "suggestion";

const CATS: { id: Cat; icon: typeof Bug; en: string; ar: string }[] = [
  { id: "bug",              icon: Bug,         en: "Something broke",        ar: "في شي معطّل" },
  { id: "confusing_screen", icon: HelpCircle,  en: "Confusing screen",       ar: "شاشة محيّرة" },
  { id: "content",          icon: BookOpen,    en: "Content feedback",       ar: "ملاحظة على المحتوى" },
  { id: "child_reaction",   icon: Baby,        en: "How my child reacted",   ar: "ردّة فعل طفلي" },
  { id: "billing",          icon: CreditCard,  en: "Payment / subscription", ar: "اشتراك أو دفع" },
  { id: "suggestion",       icon: Sparkles,    en: "A suggestion",           ar: "اقتراح" },
];

export function BetaFeedback() {
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const loc = useLocation();
  const send = useServerFn(submitFeedback);

  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState<Cat | null>(null);
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCat(null);
    setMsg("");
    setSubmitting(false);
  };

  const submit = async () => {
    if (!cat || msg.trim().length < 1) return;
    setSubmitting(true);
    try {
      const childId =
        typeof window !== "undefined" ? localStorage.getItem("jozori_active_child") : null;
      const currentRoute =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}`
          : loc.pathname;
      await send({
        data: {
          category: cat,
          message: msg.trim(),
          route: currentRoute,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
          locale: lang,
          app_version: import.meta.env.MODE,
          child_id: childId && /^[0-9a-f-]{36}$/i.test(childId) ? childId : null,
        },
      });
      toast.success(isAr ? "شكراً لك 💛 وصلتنا ملاحظتك" : "Thank you 💛 — we received it");
      setOpen(false);
      reset();
    } catch (e) {
      const detail =
        e instanceof Error && e.message ? e.message : isAr ? "خطأ غير معروف" : "Unknown error";
      console.error("[BetaFeedback] submit failed:", e);
      toast.error(
        isAr
          ? `ما قدرنا نرسل ملاحظتك: ${detail}`
          : `Couldn't send your note: ${detail}`,
      );
      setSubmitting(false);
    }
  };


  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={isAr ? "ساعدنا نطوّر جذوري" : "Help us improve Jozori"}
        className="fixed bottom-24 end-4 z-30 grid h-12 w-12 place-items-center rounded-full bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25 backdrop-blur transition-all hover:scale-105 hover:bg-primary active:scale-95"
        dir={dir}
      >
        <Heart className="h-5 w-5" />
      </button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle className={isAr ? "font-display-ar text-xl" : "text-xl"}>
              {isAr ? "ساعدنا نطوّر جذوري" : "Help us improve Jozori"}
            </DialogTitle>
            <DialogDescription>
              {isAr
                ? "ملاحظتك بتوصل مباشرة لفريقنا الصغير. كل كلمة بتفرق."
                : "Your note reaches our small team directly. Every word matters."}
            </DialogDescription>
          </DialogHeader>

          {!cat ? (
            <div className="grid grid-cols-2 gap-2 pt-1">
              {CATS.map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCat(c.id)}
                    className="flex flex-col items-start gap-1.5 rounded-2xl border border-border bg-card p-3 text-start transition-colors hover:border-primary/50 hover:bg-muted/40"
                  >
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium leading-tight">
                      {isAr ? c.ar : c.en}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3 pt-1">
              <button
                onClick={() => setCat(null)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {isAr ? "← تغيير النوع" : "← Change category"}
              </button>
              <textarea
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                maxLength={4000}
                rows={5}
                autoFocus
                placeholder={
                  isAr
                    ? "احكِ لنا بكلامك… شو صار، شو حسّيت، شو ممكن نحسّن"
                    : "Tell us in your own words — what happened, what you felt, what could be better"
                }
                className={`w-full resize-none rounded-2xl border border-border bg-background p-3 text-sm leading-relaxed outline-none focus:border-primary ${
                  isAr ? "font-display-ar text-[15px]" : ""
                }`}
              />
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">
                  {isAr
                    ? "بنسجّل الصفحة والجهاز لنفهم السياق فقط."
                    : "We attach the page and device for context only."}
                </p>
                <button
                  onClick={submit}
                  disabled={submitting || msg.trim().length < 1}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity disabled:opacity-40"
                >
                  <Send className="h-3.5 w-3.5" />
                  {submitting
                    ? isAr ? "جاري الإرسال…" : "Sending…"
                    : isAr ? "إرسال" : "Send"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
